import React, { useMemo } from 'react';
import { MODULE_KEYS } from '../../../config/rbacConfig';
import { getModuleName } from '../../../config/moduleCatalog';
import { Field as FormField, Select as FormSelect } from '../../reusable/formRecords/formFieldPrimitives';

const LABEL = 'text-[13px] font-semibold text-gray-900';
const NOTE = 'text-[11px] text-gray-400';
const ALL_MODULES_LABEL = 'All Modules';

// `label` resolves from the shared module catalog at import time — same
// load-time (not live) resolution as coreModulesConfig.js. A rename made
// elsewhere in the same session shows up here after a page refresh.
export const MODULE_SCOPE_OPTIONS = [
  { value: 'mod_1', moduleKey: MODULE_KEYS.POPULATION, registryKey: 'livestock-population', moduleNo: '1', title: 'Module 1', label: getModuleName('livestock-population') },
  { value: 'mod_2', moduleKey: MODULE_KEYS.MARKETS, registryKey: 'markets-trade', moduleNo: '2', title: 'Module 2', label: getModuleName('markets-trade') },
  { value: 'mod_3', moduleKey: MODULE_KEYS.INFRASTRUCTURE, registryKey: 'infrastructure', moduleNo: '3', title: 'Module 3', label: getModuleName('infrastructure') },
  { value: 'mod_4', moduleKey: MODULE_KEYS.WORKFORCE, registryKey: 'workforce-institutional', moduleNo: '4', title: 'Module 4', label: getModuleName('workforce-institutional') },
  { value: 'mod_5', moduleKey: MODULE_KEYS.PLANNING, registryKey: 'planning-reporting', moduleNo: '5', title: 'Module 5', label: getModuleName('planning-reporting') },
  { value: 'mod_6', moduleKey: MODULE_KEYS.CONFLICTS, registryKey: 'conflicts-dispute', moduleNo: '6', title: 'Module 6', label: getModuleName('conflicts-dispute') },
  { value: 'mod_7', moduleKey: MODULE_KEYS.ANIMAL_IDENTIFICATION, registryKey: 'animal-identification', moduleNo: '7', title: 'Module 7', label: getModuleName('animal-identification') },
  { value: 'mod_8', moduleKey: MODULE_KEYS.ANIMAL_HEALTH, registryKey: 'animal-health', moduleNo: '8', title: 'Module 8', label: getModuleName('animal-health') },
];

const OPTION_LOOKUP = new Map();

function normalizeLookupKey(value) {
  return String(value ?? '')
    .trim()
    .replace(/[_\s-]+/g, '')
    .toLowerCase();
}

MODULE_SCOPE_OPTIONS.forEach((option) => {
  [
    option.value,
    option.moduleKey,
    option.registryKey,
    option.moduleKey?.toLowerCase(),
    option.title,
    option.moduleNo,
    `module${option.moduleNo}`,
    `Module ${option.moduleNo}`,
    option.label,
  ].forEach((token) => {
    const key = normalizeLookupKey(token);
    if (key && !OPTION_LOOKUP.has(key)) {
      OPTION_LOOKUP.set(key, option);
    }
  });
});

function parseScopeTokens(value) {
  if (Array.isArray(value)) return value;

  const raw = String(value ?? '').trim();
  if (!raw) return [];

  return raw.split(/[,\s|;]+/).map((token) => token.trim()).filter(Boolean);
}

function resolveOption(token) {
  return OPTION_LOOKUP.get(normalizeLookupKey(token)) ?? null;
}

function resolveTokenToValue(token) {
  const option = resolveOption(token);
  return option?.value ?? String(token ?? '').trim();
}

function sortTokens(tokens) {
  const order = new Map(MODULE_SCOPE_OPTIONS.map((option, index) => [option.value, index]));
  return [...tokens].sort((a, b) => {
    const rankA = order.get(a) ?? Number.MAX_SAFE_INTEGER;
    const rankB = order.get(b) ?? Number.MAX_SAFE_INTEGER;
    if (rankA !== rankB) return rankA - rankB;
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  });
}

export function parseModuleScopeValue(value) {
  const tokens = parseScopeTokens(value)
    .map(resolveTokenToValue)
    .filter(Boolean);
  return [...new Set(sortTokens(tokens))];
}

export function formatModuleScopeLabel(value) {
  const tokens = parseModuleScopeValue(value);
  if (tokens.length === 0 || tokens.length === MODULE_SCOPE_OPTIONS.length) return ALL_MODULES_LABEL;

  return tokens
    .map((token) => {
      const option = resolveOption(token);
      return option ? `${option.title} - ${option.label}` : token;
    })
    .join(', ');
}

export default function ModuleScopeField({
  label = 'Module Scope',
  required = false,
  value,
  onChange,
  note,
  showShortcut = false,
  singleSelect = false,
  moduleValue = '',
  onModuleChange,
  submoduleValue = '',
  onSubmoduleChange,
  submoduleOptions = [],
  submoduleLabel = 'Sub Module',
  singleSelectLayout = 'cards',
}) {
  const selected = useMemo(() => parseModuleScopeValue(value), [value]);
  const allSelected = selected.length === MODULE_SCOPE_OPTIONS.length;
  const activeModule = useMemo(() => {
    if (!singleSelect) return null;
    return MODULE_SCOPE_OPTIONS.find((option) => (
      normalizeLookupKey(option.moduleKey) === normalizeLookupKey(moduleValue)
      || normalizeLookupKey(option.registryKey) === normalizeLookupKey(moduleValue)
      || normalizeLookupKey(option.value) === normalizeLookupKey(moduleValue)
      || normalizeLookupKey(option.label) === normalizeLookupKey(moduleValue)
    )) ?? null;
  }, [moduleValue, singleSelect]);

  const normalizedSubmoduleOptions = useMemo(
    () => (Array.isArray(submoduleOptions) ? submoduleOptions : [])
      .map((option) => {
        if (option === null || option === undefined) return null;
        if (typeof option === 'string') {
          const text = option.trim();
          return text ? { value: text, label: text } : null;
        }
        const valueKey = String(option.value ?? option.id ?? option.code ?? option.label ?? '').trim();
        if (!valueKey) return null;
        return {
          value: valueKey,
          label: String(option.label ?? option.name ?? valueKey).trim() || valueKey,
        };
      })
      .filter(Boolean),
    [submoduleOptions]
  );
  const moduleSelectValue = activeModule?.registryKey ?? activeModule?.moduleKey ?? '';
  const submoduleSelectValue = normalizedSubmoduleOptions.find((option) => (
    normalizeLookupKey(option.value) === normalizeLookupKey(submoduleValue)
    || normalizeLookupKey(option.label) === normalizeLookupKey(submoduleValue)
  ))?.value ?? String(submoduleValue ?? '');

  const emitSingleModule = (nextModuleKey) => {
    if (onModuleChange) {
      onModuleChange(nextModuleKey);
      return;
    }
    if (onChange) {
      onChange(nextModuleKey);
    }
  };

  const emitSingleSubmodule = (nextSubmoduleKey) => {
    if (onSubmoduleChange) {
      onSubmoduleChange(nextSubmoduleKey);
      return;
    }
    if (onChange) {
      onChange(nextSubmoduleKey);
    }
  };

  const setSelected = (next) => {
    const normalized = parseModuleScopeValue(next);
    onChange(normalized.join(','));
  };

  const toggle = (token) => {
    const option = resolveOption(token);
    const key = option?.value ?? String(token ?? '').trim();
    if (!key) return;

    const next = selected.includes(key)
      ? selected.filter((item) => item !== key)
      : [...selected, key];

    setSelected(next);
  };

  const clear = () => setSelected([]);
  const applyAllModules = () => setSelected(MODULE_SCOPE_OPTIONS.map((option) => option.value));

  const applyRo3Preset = () => setSelected(['mod_1', 'mod_2']);

  if (singleSelect) {
    const activeSubmodule = normalizedSubmoduleOptions.find((option) => (
      normalizeLookupKey(option.value) === normalizeLookupKey(submoduleValue)
      || normalizeLookupKey(option.label) === normalizeLookupKey(submoduleValue)
    )) ?? null;

    if (singleSelectLayout === 'dropdown') {
      return (
        <div className="grid gap-3">
          <div className="flex items-start justify-between gap-3">
            <label className={LABEL}>
              {label}{required && <span className="ml-0.5 text-red-500">*</span>}
            </label>
            {(moduleSelectValue || submoduleSelectValue) && (
              <button
                type="button"
                onClick={() => {
                  emitSingleModule('');
                  emitSingleSubmodule('');
                }}
                className="text-[11px] font-semibold text-gray-500 transition hover:text-gray-700"
              >
                Clear
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField label="Module" required={required}>
              <FormSelect
                value={moduleSelectValue}
                onChange={(event) => emitSingleModule(event.target.value)}
                options={MODULE_SCOPE_OPTIONS.map((option) => ({
                  value: option.registryKey ?? option.moduleKey ?? option.value,
                  label: option.label,
                }))}
                placeholder="Select module"
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 outline-none transition focus:border-green-300 focus:ring-2 focus:ring-green-100"
              />
            </FormField>

            <FormField label={submoduleLabel} required={required}>
              <FormSelect
                value={submoduleSelectValue}
                onChange={(event) => emitSingleSubmodule(event.target.value)}
                options={normalizedSubmoduleOptions}
                placeholder={moduleSelectValue ? `Select ${submoduleLabel.toLowerCase()}` : 'Select module first'}
                disabled={!moduleSelectValue || normalizedSubmoduleOptions.length === 0}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 outline-none transition focus:border-green-300 focus:ring-2 focus:ring-green-100 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400"
              />
            </FormField>
          </div>

          {activeModule && activeSubmodule && (
            <div className="rounded-xl border border-green-200 bg-green-50/70 px-3 py-2 text-[12px] font-medium text-green-700">
              {activeModule.title} - {activeModule.label}
              {activeSubmodule ? ` · ${activeSubmodule.label}` : ''}
            </div>
          )}

          {note && <span className={NOTE}>{note}</span>}
        </div>
      );
    }

    return (
      <div className="grid gap-3">
        <div className="flex items-start justify-between gap-3">
          <label className={LABEL}>
            {label}{required && <span className="ml-0.5 text-red-500">*</span>}
          </label>
          {(activeModule || activeSubmodule) && (
            <button
              type="button"
              onClick={() => {
                emitSingleModule('');
                emitSingleSubmodule('');
              }}
              className="text-[11px] font-semibold text-gray-500 transition hover:text-gray-700"
            >
              Clear
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {MODULE_SCOPE_OPTIONS.map((option) => {
            const active = normalizeLookupKey(activeModule?.moduleKey ?? activeModule?.value) === normalizeLookupKey(option.moduleKey);
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => emitSingleModule(option.registryKey ?? option.moduleKey ?? option.value)}
                className={`rounded-xl border px-3 py-2.5 text-left transition ${
                  active
                    ? 'border-green-200 bg-green-50 text-green-700 shadow-sm'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-green-300 hover:bg-green-50/40'
                }`}
              >
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em]">
                  {option.title}
                </div>
                <div className="mt-0.5 text-[13px] font-semibold leading-snug">
                  {option.label}
                </div>
              </button>
            );
          })}
        </div>

        {normalizedSubmoduleOptions.length > 0 && (
          <div className="grid gap-2">
            <label className={LABEL}>
              {submoduleLabel}
            </label>
            <div className="relative">
              <select
                value={submoduleValue}
                onChange={(event) => emitSingleSubmodule(event.target.value)}
                disabled={!activeModule}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 outline-none transition focus:border-green-300 focus:ring-2 focus:ring-green-100 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400"
              >
                <option value="">{activeModule ? `Select ${submoduleLabel.toLowerCase()}` : 'Select module first'}</option>
                {normalizedSubmoduleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <svg className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        )}

        {activeModule && activeSubmodule && (
          <div className="rounded-xl border border-green-200 bg-green-50/70 px-3 py-2 text-[12px] font-medium text-green-700">
            {activeModule.title} - {activeModule.label}
            {activeSubmodule ? ` · ${activeSubmodule.label}` : ''}
          </div>
        )}

        {note && <span className={NOTE}>{note}</span>}
      </div>
    );
  }

  return (
    <div className="grid gap-2">
      <div className="flex items-start justify-between gap-3">
        <label className={LABEL}>
          {label}{required && <span className="ml-0.5 text-red-500">*</span>}
        </label>
        <div className="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={applyAllModules}
            className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition ${
              allSelected
                ? 'border-green-200 bg-green-50 text-green-700'
                : 'border-gray-200 bg-white text-gray-600 hover:border-green-200 hover:bg-green-50 hover:text-green-700'
            }`}
          >
            All Modules
          </button>
          {showShortcut && (
            <button
              type="button"
              onClick={applyRo3Preset}
              className="rounded-full border border-green-200 bg-green-50 px-3 py-1 text-[11px] font-semibold text-green-700 transition hover:bg-green-100"
            >
              Use Module 1 + 2
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {MODULE_SCOPE_OPTIONS.map((option) => {
          const active = selected.includes(option.value);
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => toggle(option.value)}
              className={`rounded-xl border px-3 py-2.5 text-left transition ${
                active
                  ? 'border-green-200 bg-green-50 text-green-700 shadow-sm'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-green-300 hover:bg-green-50/40'
              }`}
            >
              <div className="text-[11px] font-semibold uppercase tracking-[0.16em]">
                {option.title}
              </div>
              <div className="mt-0.5 text-[13px] font-semibold leading-snug">
                {option.label}
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {selected.length === 0 ? (
          <span className={NOTE}>No modules selected. Use All Modules for a full assignment or pick a subset below.</span>
        ) : allSelected ? (
          <>
            <span className="inline-flex items-center gap-1 rounded-lg border border-green-200 bg-green-50 px-2.5 py-1 text-[12px] font-medium text-green-700">
              {ALL_MODULES_LABEL}
            </span>
            <button
              type="button"
              onClick={clear}
              className="text-[11px] font-semibold text-gray-500 transition hover:text-gray-700"
            >
              Clear all
            </button>
          </>
        ) : (
          <>
            {selected.map((token) => {
              const option = resolveOption(token);
              const chipLabel = option ? `${option.title} - ${option.label}` : token;
              return (
                <span
                  key={token}
                  className="inline-flex items-center gap-1 rounded-lg border border-green-200 bg-green-50 px-2.5 py-1 text-[12px] font-medium text-green-700"
                >
                  {chipLabel}
                  <button
                    type="button"
                    onClick={() => toggle(token)}
                    className="ml-0.5 text-green-400 transition hover:text-green-700"
                    aria-label={`Remove ${chipLabel}`}
                  >
                    <svg className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </span>
              );
            })}
            <button
              type="button"
              onClick={clear}
              className="text-[11px] font-semibold text-gray-500 transition hover:text-gray-700"
            >
              Clear all
            </button>
          </>
        )}
      </div>

      {note && <span className={NOTE}>{note}</span>}
    </div>
  );
}

